import {
  generateSampleNumber,
  newSample,
  Sample,
  updateSampleDocument,
  updateSampleLab,
  updateSampleNumber,
} from "@/services/backend/samples/types/sample";
import { GrowthMedium } from "@/services/backend/samples/types/growth-medium";
import { Kind } from "@/services/backend/samples/types/kind";
import { v4 } from "uuid";
import { EntityId } from "@/shared/types/entity-id";
import { Namespace } from "@/shared/types/namespace";
import { MoldDifferentiation } from "@/services/backend/samples/types/mold-differentiation";

export interface AirMicrobialSampleSet {
  id: string;
  documentId: EntityId;
  documentNamespace: Namespace | null;
  labId: EntityId;
  number: string;
  location: string;
  spot: string;
  comment: string;
  sampleTakers: string;
  value: string;
  unit: string;
  deviceId: EntityId;
  samplingVolume: number;
  caso: AirMicrobialSample | null;
  malt: AirMicrobialSample | null;
  dg18: AirMicrobialSample | null;
  moldDifferentiation: MoldDifferentiation[];
}

export function newAirMicrobialSampleSet(
  id: string,
  documentId: EntityId,
  documentNamespace: Namespace | null,
  location: string,
  spot: string,
): AirMicrobialSampleSet {
  const number = generateSampleNumber();
  return {
    id,
    documentId,
    documentNamespace,
    labId: null,
    number,
    location,
    spot,
    comment: "",
    sampleTakers: "",
    value: "",
    unit: "KBE/m³",
    deviceId: null,
    samplingVolume: 0,
    // caso and malt are always present, dg18 is optional
    caso: {
      ...newAirMicrobialSample(
        v4(),
        `${number}.CASO`,
        GrowthMedium.CASO,
        documentId,
        documentNamespace,
      ),
      location,
      spot,
    },
    malt: {
      ...newAirMicrobialSample(
        v4(),
        `${number}.MALT`,
        GrowthMedium.MALT,
        documentId,
        documentNamespace,
      ),
      location,
      spot,
    },
    dg18: null,
    moldDifferentiation: [],
  };
}

export function updateDocument(
  set: AirMicrobialSampleSet,
  id: EntityId,
  namespace: Namespace,
): AirMicrobialSampleSet {
  return {
    ...set,
    documentId: id,
    documentNamespace: namespace,
    caso: set.caso ? updateSampleDocument(set.caso, id, namespace) : null,
    malt: set.malt ? updateSampleDocument(set.malt, id, namespace) : null,
  };
}

export function updateSamplingVolumeInSet(
  sample: AirMicrobialSampleSet,
  samplingVolume: number,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, samplingVolume } : sample.caso,
    malt: sample.malt ? { ...sample.malt, samplingVolume } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, samplingVolume } : sample.dg18,
  };
}

export function updateDeviceIdInSet(
  sample: AirMicrobialSampleSet,
  deviceId: EntityId,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, deviceId } : sample.caso,
    malt: sample.malt ? { ...sample.malt, deviceId } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, deviceId } : sample.dg18,
  };
}

export function updateCreatedAtInAirSet(
  sample: AirMicrobialSampleSet,
  createdAt: string | null,
): AirMicrobialSampleSet {
  return {
    ...sample,
    caso: sample.caso ? { ...sample.caso, createdAt } : sample.caso,
    malt: sample.malt ? { ...sample.malt, createdAt } : sample.malt,
    dg18: sample.dg18 ? { ...sample.dg18, createdAt } : sample.dg18,
  };
}

export function updateAirMicrobialSetNumber(
  set: AirMicrobialSampleSet,
  number: string,
): AirMicrobialSampleSet {
  return {
    ...set,
    number,
    caso: set.caso ? updateSampleNumber(set.caso, `${number}-CASO`) : null,
    malt: set.malt ? updateSampleNumber(set.malt, `${number}-MALT`) : null,
    dg18: set.dg18 ? updateSampleNumber(set.dg18, `${number}-DG18`) : null,
  };
}

export function updateAirMicrobialSetLab(
  set: AirMicrobialSampleSet,
  labId: EntityId,
): AirMicrobialSampleSet {
  return {
    ...set,
    labId,
    caso: set.caso ? updateSampleLab(set.caso, labId) : null,
    malt: set.malt ? updateSampleLab(set.malt, labId) : null,
    dg18: set.dg18 ? updateSampleLab(set.dg18, labId) : null,
  };
}

export function updateAirMicrobialSetComment(
  set: AirMicrobialSampleSet,
  comment: string,
): AirMicrobialSampleSet {
  return {
    ...set,
    comment,
  };
}

export function updateAirMicrobialSetCaso(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.CASO) {
    return set;
  }

  return {
    ...set,
    caso: sample,
  };
}

export function updateAirMicrobialSetMalt(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.MALT) {
    return set;
  }

  return {
    ...set,
    malt: sample,
  };
}

export function updateAirMicrobialSetDg18(
  set: AirMicrobialSampleSet,
  sample: AirMicrobialSample,
): AirMicrobialSampleSet {
  if (sample.growthMedium !== GrowthMedium.DG18) {
    return set;
  }

  return {
    ...set,
    dg18: sample,
  };
}

export function activateAirMicrobialSetSample(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): AirMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: set.caso ?? {
          ...newAirMicrobialSample(
            v4(),
            `${set.number}.CASO`,
            GrowthMedium.CASO,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: set.malt ?? {
          ...newAirMicrobialSample(
            v4(),
            `${set.number}.MALT`,
            GrowthMedium.MALT,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: set.dg18 ?? {
          ...newAirMicrobialSample(
            v4(),
            `${set.number}.DG18`,
            GrowthMedium.DG18,
            set.documentId,
            set.documentNamespace,
          ),
          location: set.location,
          spot: set.spot,
          labId: set.labId,
        },
      };
    default:
      return set;
  }
}

export function deactivateAirMicrobialSetSample(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): AirMicrobialSampleSet {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return {
        ...set,
        caso: null,
      };
    case GrowthMedium.MALT:
      return {
        ...set,
        malt: null,
      };
    case GrowthMedium.DG18:
      return {
        ...set,
        dg18: null,
      };
    default:
      return set;
  }
}

export function airMicrobialSampleIsActivated(
  set: AirMicrobialSampleSet,
  growthMedium: GrowthMedium,
): boolean {
  switch (growthMedium) {
    case GrowthMedium.CASO:
      return !!set.caso;
    case GrowthMedium.MALT:
      return !!set.malt;
    case GrowthMedium.DG18:
      return !!set.dg18;
    default:
      return false;
  }
}

export interface AirMicrobialSample extends Sample {
  growthMedium: GrowthMedium;
  samplingVolume: number;
  deviceId: EntityId;
  value: string;
  unit: string;
}

function newAirMicrobialSample(
  id: string,
  number: string,
  growthMedium: GrowthMedium,
  documentId: EntityId,
  documentNamespace: Namespace | null,
): AirMicrobialSample {
  const sample = newSample(
    id,
    number,
    Kind.AirMicrobial,
    documentId,
    documentNamespace,
  );

  return {
    ...sample,
    growthMedium,
    samplingVolume: 0,
    deviceId: null,
    value: "",
    unit: "KBE/m³",
  };
}

export function updateSamplingVolume(
  sample: AirMicrobialSample,
  samplingVolume: number,
): AirMicrobialSample {
  return {
    ...sample,
    samplingVolume,
  };
}

export function updateDeviceId(
  sample: AirMicrobialSample,
  deviceId: EntityId,
): AirMicrobialSample {
  return {
    ...sample,
    deviceId,
  };
}

export function updateValue(
  sample: AirMicrobialSample,
  value: string,
): AirMicrobialSample {
  return {
    ...sample,
    value,
  };
}
